import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSalesPayment } from "../redux/actions/salesPaymentActions";
import {
  Autocomplete,
  TextField,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import FileAutocomplete from "./FileAutocomplete";
import CustomAutocomplete from "./CustomAutocomplete";
import { useLocation } from "react-router-dom";
import { InputAdornment } from "@mui/material";
import { listSalesPayments } from "../redux/actions/salesPaymentActions";
import { textFieldStyles } from "../styles/textFieldStyles";
import { smallTitle } from "../styles/typography";
import { formatDate } from "../utils/dateUtils";
const SalesPaymentForm = ({ onClose, files, relationships, popupData }) => {
  const { t, ready } = useTranslation([
    "common",
    "forms",
    "messages",
    "buttons",
    "pages",
  ]);
  const location = useLocation();
  const locationCaseId = location.state?.caseId;
  const storedCaseId = localStorage.getItem("selectedCaseId");
  
  // Extract case ID from URL if not provided in state
  const urlCaseId = window.location.pathname.match(/\/files\/([^/]+)/)?.[1];
  const initialCase = locationCaseId || storedCaseId || urlCaseId || "";

  const initialFrom = popupData?.from?._id || "";
  const initialTo = popupData?.to?._id || "";

  const [formData, setFormData] = useState({
    case: initialCase,
    from: initialFrom,
    to: initialTo,
    date: "",
    journal: "",
    amount: "",
    type: "payment",
    comment: "",
    salesDocuments: [],
  });

  const dispatch = useDispatch();
  const { salesDocuments } = useSelector((state) => state.salesDocumentList);

  const [filteredRelationships, setFilteredRelationships] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [filteredSalesDocuments, setFilteredSalesDocuments] = useState([]);
  const [totalDueAmount, setTotalDueAmount] = useState(0);

  const [formError, setFormError] = useState("");
  const { user } = useSelector((state) => state.userLoaded);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);

  // Auto-populate case, from, and to fields when case is available
  useEffect(() => {
    if (initialCase && files.length > 0) {
      const selectedFile = files.find((file) => file._id === initialCase);
      if (selectedFile) {
        // Auto-populate case field
        setFormData(prev => ({
          ...prev,
          case: initialCase
        }));

        // Auto-populate from field (first office member)
        if (selectedFile.officeMembers && selectedFile.officeMembers.length > 0) {
          const fromMember = selectedFile.officeMembers[0].member;
          setFormData(prev => ({
            ...prev,
            from: fromMember._id
          }));
        }

        // Auto-populate to field (first party/client)
        if (selectedFile.parties && selectedFile.parties.length > 0) {
          const toParty = selectedFile.parties[0].name;
          setFormData(prev => ({
            ...prev,
            to: toParty._id
          }));
        }

        // Auto-populate date with today's date
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({
          ...prev,
          date: today
        }));
      }
    }
  }, [initialCase, files]);

  useEffect(() => {
    if (formData.case) {
      const selectedFile = files.find((file) => file._id === formData.case);
      if (selectedFile) {
        const officeMembers = selectedFile.officeMembers.map(
          (member) => member.member
        );
        const clients = selectedFile.parties.map((party) => party.name);
        setFilteredRelationships(officeMembers);
        setFilteredClients(clients);
        setFilteredSalesDocuments(
          salesDocuments.filter(
            (doc) => doc.case?._id === formData.case && doc.status !== "paid"
          )
        );
      }
    }
  }, [formData.case, files, salesDocuments]);

  useEffect(() => {
    // When the case changes, reset amount and salesDocuments (checkboxes)
    setFormData((prev) => ({
      ...prev,
      amount: "",
      salesDocuments: [],
    }));
    setTotalDueAmount(0); // Optionally reset the total due amount as well
  }, [formData.case]);

  useEffect(() => {
    // Auto-select first "From" if available and not already set
    if (
      filteredRelationships.length > 0 &&
      (!formData.from ||
        !filteredRelationships.some((rel) => rel._id === formData.from))
    ) {
      setFormData((prev) => ({
        ...prev,
        from: filteredRelationships[0]._id,
      }));
    }
    // Auto-select first "To" if available and not already set
    if (
      filteredClients.length > 0 &&
      (!formData.to ||
        !filteredClients.some((client) => client._id === formData.to))
    ) {
      setFormData((prev) => ({
        ...prev,
        to: filteredClients[0]._id,
      }));
    }
  }, [filteredRelationships, filteredClients]);

  const handleCaseChange = (event, newValue) => {
    if (newValue) {
      const selectedFile = files.find((file) => file._id === newValue._id);
      const officeMembers = selectedFile.officeMembers.map(
        (member) => member.member
      );
      const clients = selectedFile.parties.map((party) => party.name);

      // Autofill logic
      const currentUserId = user?._id || ""; // Get current user from redux
      const firstClient =
        selectedFile.parties && selectedFile.parties.length > 0
          ? selectedFile.parties[0]
          : null;

      setFormData({
        ...formData,
        case: newValue._id || "",
        from: currentUserId,
        to: firstClient ? firstClient._id : "",
        date: new Date().toISOString().split("T")[0], // yyyy-mm-dd
      });

      setFilteredRelationships(officeMembers);
      setFilteredClients(clients);
      setFilteredSalesDocuments(
        salesDocuments.filter(
          (doc) => doc.case?._id === newValue._id && doc.status !== "paid"
        )
      );
    } else {
      setFilteredRelationships([]);
      setFilteredClients([]);
      setFilteredSalesDocuments([]);
      setFormData({
        ...formData,
        case: "",
        from: "",
        to: "",
        date: "",
      });
    }
  };

  const handleSalesDocumentSelect = (event, salesDocument) => {
    const selected = event.target.checked;
    setFormData((prevFormData) => {
      const updatedSalesDocuments = selected
        ? [...prevFormData.salesDocuments, salesDocument]
        : prevFormData.salesDocuments.filter(
            (doc) => doc._id !== salesDocument._id
          );
      const updatedTotalDueAmount = updatedSalesDocuments.reduce(
        (acc, doc) => acc + doc.totalDue,
        0
      );
      setTotalDueAmount(updatedTotalDueAmount);
      return {
        ...prevFormData,
        salesDocuments: updatedSalesDocuments,
        amount: updatedTotalDueAmount.toString(),
      };
    });
  };

  const calculatePaidNow = (doc) => {
    const totalAmount = parseFloat(formData.amount);
    let remainingAmount = totalAmount;

    for (const selectedDoc of formData.salesDocuments) {
      const paymentAmount = Math.min(remainingAmount, selectedDoc.totalDue);
      if (selectedDoc._id === doc._id) {
        return paymentAmount;
      }
      remainingAmount -= paymentAmount;
    }
    return 0;
  };

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;
    const amount = parseFloat(inputValue);

    // Allow empty input for better UX while typing
    if (inputValue === "") {
      setFormData({ ...formData, amount: "" });
      return;
    }

    // Validate number format and range
    if (isNaN(amount)) {
      setFormError(t("messages:please_enter_valid_number"));
      return;
    }

    if (amount < 0) {
      setFormError(t("messages:amount_cannot_be_negative"));
      return;
    }

    if (amount > totalDueAmount) {
      setFormError(`Maximum amount allowed is ${totalDueAmount.toFixed(2)}`);
      setFormData({ ...formData, amount: inputValue });
      return;
    }

    // Valid amount
    setFormError("");
    setFormData({ ...formData, amount: inputValue });
  };

  const handleCommentChange = (e) => {
    setFormData({ ...formData, comment: e.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const totalAmount = parseFloat(formData.amount);
      let remainingAmount = totalAmount;

      const updatedSalesDocuments = formData.salesDocuments.map((doc) => {
        const paymentAmount = Math.min(remainingAmount, doc.totalDue);
        remainingAmount -= paymentAmount;
        return { ...doc, paymentAmount };
      });

      const paymentData = {
        ...formData,
        salesDocuments: updatedSalesDocuments,
      };
      dispatch(createSalesPayment(paymentData));
      // setTimeout(() => {
      //   onClose();
      // }, 3000);
    } catch (error) {
      console.log({
        open: true,
        message: t("messages:error.failed_to_create_sales_payment"),
        severity: "error",
      });
    }
  };

  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (userRole === "Manager") {
      return true; // Managers can see all files
    } else if (userRole === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        return (
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (userRole === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  if (!ready) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      id="sales-payment-form"
      onSubmit={handleSubmit}
      sx={{ p: "30px 10px" }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FileAutocomplete
            options={filteredFiles}
            value={files.find((file) => file._id === formData.case) || null}
            onChange={handleCaseChange}
            label={t("forms:labels.case")}
            name="case"
          />
        </Grid>
        <Grid item xs={6}>
          <CustomAutocomplete
            options={filteredRelationships}
            value={
              filteredRelationships.find((rel) => rel._id === formData.from) ||
              null
            }
            onChange={(event, newValue) =>
              setFormData({ ...formData, from: newValue?._id || "" })
            }
            label={t("forms:labels.from")}
            filterType="Member"
            addNewLabel={t("forms:addLabels.addOwner")}
            addNewPath="/relationships/create"
            showIcon={false}
            showAddNewOption={false}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomAutocomplete
            options={filteredClients}
            value={
              filteredClients.find((client) => client._id === formData.to) ||
              null
            }
            onChange={(event, newValue) =>
              setFormData({ ...formData, to: newValue?._id || "" })
            }
            label={t("forms:labels.to")}
            filterType="Party"
            addNewLabel={t("forms:addLabels.addOwner")}
            addNewPath="/relationships/create"
            showIcon={false}
            showAddNewOption={false}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={t("common:date")}
            type="date"
            sx={textFieldStyles({ width: "100%" })}
            size="small"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            options={[
              t("forms:labels.cash"),
              t("forms:labels.bank"),
              t("forms:labels.third_party_funds"),
            ]}
            getOptionLabel={(option) => option}
            onChange={(event, newValue) =>
              setFormData({ ...formData, journal: newValue || "" })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("forms:labels.journal")}
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={t("forms:labels.payment_amount")}
            size="small"
            fullWidth
            required
            type="number"
            inputProps={{
              step: "0.01",
              min: "0",
              max: totalDueAmount,
              "aria-label": "Payment amount input",
            }}
            value={formData.amount}
            onChange={handleAmountChange}
            error={!!formError}
            helperText={
              formError || `Maximum amount: €${totalDueAmount.toFixed(2)}`
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">€</InputAdornment>
              ),
              sx: {
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    "-webkit-appearance": "none",
                  },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: formError ? "#f8d7da" : "#fff",
                borderRadius: "0",

                boxShadow: "0 2px 5px rgba(0,103,192,0.05)",
                "&:hover": {
                  backgroundColor: formError ? "#f5c6cb" : "#fafafa",
                },
                "&.Mui-focused": {
                  backgroundColor: formError ? "#f0f7ff" : "#f0f7ff",
                },
                "& fieldset": {
                  borderColor: formError ? "#dc3545" : "d1d1d1",
                },
              },
              "& .MuiFormHelperText-root": {
                color: formError ? "#dc3545" : "text.secondary",
              },
              "& .css-1q55ijt-MuiInputBase-input-MuiOutlinedInput-input": {
                padding: "5px 14px",
              },
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            options={[t("forms:labels.payment"), t("forms:labels.adjustment")]}
            defaultValue={t("forms:labels.payment")}
            getOptionLabel={(option) => option}
            onChange={(event, newValue) =>
              setFormData({ ...formData, type: newValue || "" })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("common:type")}
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t("forms:labels.comment")}
            sx={textFieldStyles({ width: "100%" })}
            size="small"
            fullWidth
            value={formData.comment}
            onChange={handleCommentChange}
          />
        </Grid>
        <Grid item xs={12}>
          {formData.case && filteredSalesDocuments.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                    }}
                  >
                    {t("common:table.headers.select")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                      width: "150px",
                    }}
                  >
                    {t("common:type")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                      width: "150px",
                    }}
                  >
                    {t("common:date")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                      width: "150px",
                    }}
                  >
                    {t("common:total")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                      width: "150px",
                    }}
                  >
                    {t("common:table.headers.totalDue")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                      width: "150px",
                    }}
                  >
                    {t("common:status")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      padding: "0px",
                      width: "150px",
                    }}
                  >
                    {t("common:table.headers.paidNow")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSalesDocuments.map((doc) => (
                  <TableRow
                    key={doc._id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f5f5f5", // Change this to your desired hover color
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      <Checkbox
                        checked={formData.salesDocuments.some(
                          (selectedDoc) => selectedDoc._id === doc._id
                        )}
                        onChange={(event) =>
                          handleSalesDocumentSelect(event, doc)
                        }
                        sx={{
                          "&.MuiCheckbox-root": {
                            padding: "5px", // Change the color of the checkbox
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      {t(`pages:finance.${doc.type}`) || doc.type}
                    </TableCell>
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      {formatDate(doc.date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      {doc.total}
                    </TableCell>
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      {doc.totalDue}
                    </TableCell>
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      {t(`common:${doc.status}`) || doc.status}
                    </TableCell>
                    <TableCell
                      sx={{
                        // borderBottom: "none",
                        padding: "0 8px",
                      }}
                    >
                      {calculatePaidNow(doc)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography
              variant="body1"
              color="textSecondary"
              align="center"
              sx={{
                ...smallTitle,
                backgroundColor: "#eeeeee",
                color: "#000000",
                padding: 2,
              }}
            >
              {t("messages:info.no_invoices_available")}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesPaymentForm;
